<template>
  <div>
    <popup-hint hint="blending">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Blending">
          <template v-slot:left v-if="incompleteCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{incompleteCount}} incomplete blend{{incompleteCount===1?'':'s'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.completeStatus='0';filters.formulaId=null;filters.from=null;filters.to=null;filters.number=''})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>

          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <production-blending-preferences/>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Blending operations are recorded here.
      </template>
    </popup-hint>

    <purtrak-table name="blending" objectType="blends" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="blends2" v-model="selected"
                   fields="id,date,number,completed,formulaId,formulaName,comments,duration,outputName,lot,blendToVolume,volume,litres,value,producedUnits"
                   :listFilters="listFilters"
                   @dblclick="blend => $router.push({name:'ProductionBlendingEdit',params:{blendId:blend.id}})"
                   :item-class="blend => (blend.completed ? '' : 'warning--text')"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   title="Blends"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',filters.completeStatus!=null?(filters.completeStatus=='1'?'Completed':'Incomplete'):'',filters.formulaId!=null?(formulas.length>3 ? formulas.find(f=>f.id===filters.formulaId).name : ''):'']}"
                   download-endpoint="blendsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.completeStatus=null;filters.formulaId=null;filters.from=null;filters.to=null;filters.number=''">
          <v-list-item>
            <filter-search label="Number" v-model="filters.number"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item v-if="preferences.blendIncomplete">
            <v-autocomplete label="Completed" dense outlined :items="[{value:null,text:'All'},{divider:true},{value:'1',text:'Completed'},{value:'0',text:'Incomplete'}]" v-model="filters.completeStatus"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Formula" dense outlined :items="formulas" item-text="name" item-value="id" v-model="filters.formulaId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.number!=''" close small class="ml-1" @click:close="filters.number=''">#: {{filters.number}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.completeStatus!=null" close small class="ml-1" @click:close="filters.completeStatus=null">{{filters.completeStatus=='1'?'Completed':'Incomplete'}}</v-chip>
          <v-chip v-show="filters.formulaId!=null" close small class="ml-1" @click:close="filters.formulaId=null">{{formulas.length>3 ? formulas.find(f=>f.id===filters.formulaId).name : ''}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'ProductionBlendingEditNew'}"><v-list-item-title>Create blend</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'ProductionBlendingFormulas'}"><v-list-item-title>Formulas</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Blending',to:{name:'ProductionBlending'}}]},query:{to:'reports/production/ReportsBlendingActivity'}}"><v-list-item-title>Blending activity</v-list-item-title></v-list-item>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="printBlendBatch()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{item,value,printing}">
        {{value | formatDateTime}}
        <div v-if="!item.completed && !printing" class="text-caption"><em>INCOMPLETE</em></div>
      </template>
      <template v-slot:item.formulaName="{item,printing}">
        {{item.formulaName}}
      </template>
      <template v-slot:item.outputName="{item,printing}">
        {{item.outputName}}
        <div v-if="!printing" class="text-caption">{{item.comments}}</div>
      </template>
      <template v-slot:item.lot="{item,value,printing}">
        <template v-if="printing">{{value}}</template>
        <v-hover v-else-if="item.lot" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Blending',to:{name:'ProductionBlending'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:1}})"
             :class="{'text-decoration-underline':hover}">{{item.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:item.volume="{item,printing}">
        <template v-if="item.blendToVolume">
          {{item.litres | number(3)}} litres
          <div v-if="!printing && item.producedUnits==='LAA'" class="text-caption">{{item.volume | number(3)}} LAA</div>
        </template>
        <template v-else>{{item.volume | number(3)}} {{item.producedUnits}}</template>
      </template>
      <template v-slot:item.litres="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.value="{value}">
        ${{value | number(2)}}
      </template>
      <template v-slot:item.duration="{value}">
        {{value | duration}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionBlendingEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Blend operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small >mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ProductionBlendingEdit',params:{blendId:item.id}}"><v-list-item-title>Details</v-list-item-title></v-list-item>
            <v-list-item @click="printBlend(item.id,null,true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Blending',to:{name:'ProductionBlending'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:1}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
            <v-list-item v-if="audit" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item @click="updateComments(item)"><v-list-item-title>Update comments</v-list-item-title></v-list-item>
              <v-list-item @click="blend=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete blend</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
            <v-simple-table dense class="px-16 text-caption" :class="expansionPanelClass" style="width:100%">
              <thead>
                <tr>
                  <th :class="expansionPanelClass" width="25%">Input</th>
                  <th :class="expansionPanelClass" width="25%">Source</th>
                  <th :class="expansionPanelClass" width="10%">Lot</th>
                  <th class="text-right" :class="expansionPanelClass" width="15%" v-if="values && item.completed">Value</th>
                  <th class="text-right" :class="expansionPanelClass" width="15%">{{item.completed?'':'Pending '}}Quantity</th>
                  <th v-if="item.producedUnits==='LAA'" class="text-right" :class="expansionPanelClass" width="10%">Percent</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(input,index) of item.inputs" :key="index">
                  <td class="text-caption">{{input.typeName}}</td>
                  <td class="text-caption">{{input.type === 'Item' ? '' : input.vesselName}}</td>
                  <td class="text-caption">
                    <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Blending',to:{name:'ProductionBlending'}}]},query:{to:'reports/inventory/ReportsLot',id:input.type==='Item'?input.id:input.vesselId,lot:input.lot,forward:0}})">{{input.lot}}</a>
                  </td>
                  <td class="text-caption text-right" v-if="values && item.completed">
                    {{input.value | currency}}
                  </td>
                  <td class="text-caption text-right">
                    {{input.type === 'Item' || input.type === 'Wine' && item.producedUnits !== 'LAA' ? input.quantity : input.laa | number(input.precision)}}
                    {{input.type === 'Wine' && item.producedUnits !== 'LAA' ? 'litres' : input.units}}
                  </td>
                  <td v-if="item.producedUnits==='LAA'" class="text-caption text-right">{{input.percent | number(1)}}%</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected blends':'this blend'" @delete="performDelete(blend.id)"/>
    <component :is="dialog" v-bind="dialogProperties" @done="dialog = null"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import productionBlendingPrintBlend from '@/views/production/blending/productionBlendingPrintBlend'
import ProductionBlendingPreferences from '@/views/production/blending/ProductionBlendingPreferences'

export default {
  name: 'ProductionBlending',
  mixins: [productionBlendingPrintBlend,performDelete],
  components: {ProductionBlendingPreferences},
  data() {
    return {
      incompleteCount: 0,
      filters: {
        number: '',
        from: null,
        to: null,
        future: '1',
        completeStatus: null,
        formulaId: null
      },
      showStatus: false,
      dateDisplay: '',
      preferences: {},
      blend: {},
      completeOptions: [{name:'Completed/Incomplete',value:0,divider:true},{name:'Completed only',value:1},{name:'Incomplete only',value:2}],
      formulas: [{name:'All',id:null},{name:'None',id:''},{divider:true}],
      showDelete: false,
      deleteMultiple: false,
			dialog: null,
      dialogProperties: {},
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'formulaName', visible:true},
                       { value: 'outputName', visible:true},
                       { value: 'comments', visible:false},
                       { value: 'lot', visible:true},
                       { value: 'duration' ,visible:false},
                       { value: 'volume', visible:true},
                       { value: 'litres', visible:false},
                       { value: 'value', visible:true}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: '#', value: 'number', width:'10%',sortable:true},
                { text: 'FORMULA', value: 'formulaName', width:'20%',sortable:true},
                { text: 'PRODUCED', value: 'outputName', width:'25%',sortable:true},
                { text: 'COMMENTS', value: 'comments', width:'25%',sortable:false},
                { text: 'DURATION', value: 'duration', width:'15%',sortable:true, omit:() => !this.preferences.blendDuration},
                { text: 'LOT', value: 'lot', width:'10%',sortable:true},
                { text: 'VOLUME', value: 'volume', width:'10%',sortable:true,align:'right'},
                { text: 'LITRES', value: 'litres', width:'10%',sortable:true,align:'right'},
                { text: 'VALUE', value: 'value', width:'10%',sortable:true,align:'right'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    audit() {
      return this.company.allowAudit && this.company.permissions.includes('audit');
    },
    listFilters() {
      const filters = [];
      if (this.filters.number)
        filters.push({field:'number', comparator:'contains', value:this.filters.number});
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.completeStatus == '1' || this.filters.completeStatus == '0')
        filters.push({field:'completed', comparator:'eq', value:this.filters.completeStatus == '1' ? 'true' : 'false'});
      if (this.filters.formulaId === '')
        filters.push({field:'formulaId', comparator:'isNull', value:''});
      else if (this.filters.formulaId !== null)
        filters.push({field:'formulaId', comparator:'eq', value:this.filters.formulaId});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.$lazyListLoad(this.formulas, 'formulas2', 'id,name', 100);

    this.loading = true;
    const results = await this.$apiCall(`query ProductionBlending {
                                              blendingPreferences {
                                                blendDuration,blendIncomplete
                                              }
                                            }`,
                                        {});
    if (!results.error)
      this.preferences = results.data.blendingPreferences;
    await this.getStatus();
    this.loading = false;

    if (!['ProductionBlendingEdit', 'ProductionBlendingEditNew','ProductionBlendingProcessess'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.incompleteCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async getStatus() {
      const results = await this.$apiCall(`query ProductionBlending {
                                             blendsIncomplete
                                          }`);
      if (!results.error)
        this.incompleteCount = results.data.blendsIncomplete;
    },
    async expand(item, value) {
      if (value && !item.items) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionBlending($id:ID!) {
                                                  blend(id:$id) {
                                                    inputs{type,typeName,vesselId,id,vesselName,lot,quantity,laa,value,units,precision,percent},
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'inputs', results.data.blend.inputs);
        this.loading = false;
      }
    },
    updateComments(blend) {
      this.dialogProperties = {item:blend, operation:'blendUpdateComments'};
      this.dialog = () => import(`@/views/production/ProductionUpdateComments`);
    },
    async printBlendBatch() {
      const lines = [];
      let n = this.selected.length;
      for (const blend of this.selected) {
        lines.push(... await this.printBlend(blend.id, null, false));
        if (--n)
          lines.push(`<div class="page-break"/>`);
      }
      this.printHtml('Blend',lines.join(''));
    }
  }
}
</script>